/**
 * The frame of an unauthenticated public page (a survey's `/s/:token`, a
 * volunteer signup page's `/v/:token`, and the staff preview of a survey): a
 * full-width muted background, the centred card (42rem; `wide` = 64rem for a
 * page whose content needs room, the volunteer time grid and table), and the fixed safety
 * footer ("Never enter passwords or payment details…", "Powered by Relevé",
 * and the Relevé privacy policy link). Hosts render their loading / error /
 * unavailable states inside it too.
 *
 * On a phone (below `sm`) there is no card: the content runs edge to edge on
 * the page background with a 16px gutter (`PUBLIC_PAGE_GUTTER`), so the page
 * reads as the phone's own screen rather than a box inside it. From `sm` up
 * it is the centred, bordered card on the muted background. Content that
 * bleeds to the edge (a sticky tray, a banner) uses the matching negative
 * margins.
 *
 * The footer is fixed Relevé copy, never page data: respondents' privacy
 * notice (docs/GDPR_COMPLIANCE.md).
 */
import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from '@/hooks/useTranslation';
import { LEGAL_DOCUMENT_URLS } from '@/lib/legalDocuments';
import { cn } from '@/lib/utils';

export interface PublicPageShellProps {
  children: ReactNode;
  /** Merged onto the outer frame (the survey preview swaps `min-h-screen` for `min-h-full`). */
  className?: string;
  /** The footer's safety line. Default `common.publicPage.safety`. */
  safetyNote?: string;
  /** `wide` widens the card to 64rem (`max-w-5xl`). Default 42rem. */
  width?: 'default' | 'wide';
}

export function PublicPageShell({ children, className, safetyNote, width = 'default' }: Readonly<PublicPageShellProps>) {
  const { t } = useTranslation('common');
  return (
    // w-full: the public page renders inside SidebarProvider's `flex w-full`
    // wrapper, where a flex item without it shrinks to its content (OrgSelect
    // precedent). The card is capped at 42rem (672px, the same as max-w-2xl),
    // or 64rem when wide. self-start: that wrapper is a fixed-height flex
    // row, so a stretched frame stopped at the viewport and a long page ran
    // past its background; min-h-screen still fills a short one.
    <div className={cn('relative flex min-h-screen w-full flex-col self-start bg-card sm:bg-muted/30', className)}>
      <main className={cn('mx-auto w-full flex-1 sm:px-4 sm:py-10', width === 'wide' ? 'max-w-5xl' : 'max-w-[42rem]')}>
        <Card className="rounded-none border-0 shadow-none sm:rounded-lg sm:border sm:shadow-sm">
          <CardContent className="px-4 pb-8 pt-4 sm:p-6">{children}</CardContent>
        </Card>
      </main>
      <footer className="border-t bg-background px-4 py-4 text-center text-xs text-muted-foreground">
        <p>{safetyNote ?? t('publicPage.safety')}</p>
        <p className="mt-1">
          {t('publicPage.poweredBy')}
          {' · '}
          <a
            href={LEGAL_DOCUMENT_URLS['privacy-policy']}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            {t('publicPage.privacy')}
          </a>
        </p>
      </footer>
    </div>
  );
}
